import {
  FunctionComponent,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useState,
} from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';

import { dimmerAnimation } from '../../animation/dimmerAnimation';
import { Button } from '../../atoms/button';
import ChevronDisplay from '../../atoms/ChevronDisplay';
import { SafeFormattedMessageWithoutSpread } from '../../formatters';
import { SafeFormattedMessageInput } from '../../formatters/SafeFormattedMessageWithoutSpread';

import {
  $AccordionContainer,
  $AccordionHeaderContentTitle,
  $AccordionHeaderMinimal,
  $Separator,
} from './Accordion.styles';
import { ACCORDION_ROLE } from './Accordion.types';
import AccordionContent from './Content';
import AccordionHeader from './Header';
import { AccordionAdditionalTitleContent } from './Header/AccordionAdditionalTitleContent';

export interface AccordionProps {
  isOpen?: boolean;
  isDisabled?: boolean;
  isErrored?: boolean;
  naked?: boolean;
  initialIsOpen?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
  headerRightChildren?: ReactNode;
  title?: SafeFormattedMessageInput;
  customTitle?: ReactElement;
  dataTestId?: string;
  role?: ACCORDION_ROLE;
  componentThemeName?: string;
  isChevronOnRightSide?: boolean;
  isListTheme?: boolean;
  withoutMargin?: boolean;
  isColored?: boolean;
  shouldNotRenderIfEmpty?: boolean;
  additionalTitleContent?: string;
  isMinimal?: boolean;
}

const Accordion: FunctionComponent<PropsWithChildren<AccordionProps>> = ({
  title,
  customTitle,
  additionalTitleContent = '',
  isOpen = undefined,
  initialIsOpen = false,
  isDisabled = false,
  isErrored = false,
  onClick = undefined,
  headerRightChildren = undefined,
  children = undefined,
  naked = false,
  dataTestId = undefined,
  role = ACCORDION_ROLE.PRIMARY,
  componentThemeName,
  isChevronOnRightSide,
  isListTheme = false,
  withoutMargin = false,
  isColored = false,
  shouldNotRenderIfEmpty = false,
  isMinimal = false,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(initialIsOpen);

  const handleClick = (
    event: MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => {
    if (onClick) {
      onClick(event);
    }

    if (isOpen === undefined) {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  if (shouldNotRenderIfEmpty && !children) {
    return <></>;
  }

  return (
    <$AccordionContainer
      $naked={naked}
      data-testid={dataTestId}
      $isList={isListTheme}
      $withoutMargin={withoutMargin}
      {...dimmerAnimation}
    >
      {!isMinimal && (
        <AccordionHeader
          isOpen={isOpen !== undefined ? isOpen : internalIsOpen}
          isDisabled={isDisabled}
          isErrored={isErrored}
          onClick={handleClick}
          headerRightChildren={headerRightChildren}
          role={role}
          isChevronOnRightSide={isChevronOnRightSide}
          componentThemeName={componentThemeName}
          isListTheme={isListTheme}
          isColored={isColored}
          hasAdditionalTitleContent={!!additionalTitleContent}
        >
          <$AccordionHeaderContentTitle>
            {title && <SafeFormattedMessageWithoutSpread message={title} />}
            {customTitle}
            <AccordionAdditionalTitleContent
              additionalTitleContent={additionalTitleContent}
            />
          </$AccordionHeaderContentTitle>
        </AccordionHeader>
      )}

      {isMinimal && (
        <$AccordionHeaderMinimal>
          <Button
            tertiary
            icon={
              <ChevronDisplay
                isOpen={isOpen !== undefined ? isOpen : internalIsOpen}
              />
            }
            type={BUTTON_TYPES.BUTTON}
            onClick={handleClick}
            disabled={isDisabled}
            isError={isErrored}
          >
            {title && <SafeFormattedMessageWithoutSpread message={title} />}
            {customTitle}
          </Button>

          <$Separator />
        </$AccordionHeaderMinimal>
      )}

      <AccordionContent
        naked={naked}
        isOpen={isOpen !== undefined ? isOpen : internalIsOpen}
        componentThemeName={componentThemeName}
        isListTheme={isListTheme}
      >
        {children}
      </AccordionContent>
    </$AccordionContainer>
  );
};

export default Accordion;
